import type {AlertaPersonalizada} from '@cnc/contracts';
import {useState} from 'react';
import {Link} from 'react-router';
import {useEditarAlerta} from '../api/alertas';
import {ErrorDeApi} from '../api/cliente';
import {tr} from '../i18n/idioma';
import {fecha, numero} from '../utilidades/formato';
import {FormularioDeAlerta} from './formulario-de-alerta';
import {usePermisos} from './sesion-contexto';

// Hoy en Colombia (AAAA-MM-DD), para la fecha inicial de una alerta nueva.
const hoyEnBogota = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Bogota',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

// Alertas de una exportacion (decision 0037): las suyas y las que comparte con
// otras exportaciones; desde aqui se crean (con esta exportacion ya elegida),
// se resuelven y se reabren.
export function AlertasDeExportacion({codigo, alertas}: {codigo: string; alertas: AlertaPersonalizada[]}) {
  const permisos = usePermisos();
  const editar = useEditarAlerta();
  const [nueva, setNueva] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cambiarEstado = async (a: AlertaPersonalizada) => {
    setError(null);
    try {
      await editar.mutateAsync({id: a.id, cambios: {estado: a.estado === 'resuelta' ? 'activa' : 'resuelta'}});
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : tr('No se pudo guardar.', 'Could not save.'));
    }
  };
  return (
    <section
      aria-label={tr('Alertas', 'Alerts')}
      className="rounded-lg border border-linea bg-white p-4"
      data-prueba="alertas-de-exportacion"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="font-medium">
          {tr('Alertas', 'Alerts')} ({numero(alertas.length)})
        </h2>
        {permisos.editar && !nueva && (
          <button
            type="button"
            onClick={() => setNueva(true)}
            className="text-sm font-medium text-verde hover:underline"
          >
            {tr('＋ Alerta', '＋ Alert')}
          </button>
        )}
      </div>
      {nueva && (
        <div className="mt-2">
          <FormularioDeAlerta hoy={hoyEnBogota()} codigos={[codigo]} onListo={() => setNueva(false)} />
        </div>
      )}
      {error && (
        <p className="mt-2 text-sm text-alerta" role="alert">
          {error}
        </p>
      )}
      {alertas.length === 0 ? (
        <p className="mt-2 text-sm text-apagado">
          {tr('Sin alertas para esta exportación.', 'No alerts for this export.')}
        </p>
      ) : (
        <ul className="mt-2 space-y-1.5 text-sm">
          {alertas.map(a => (
            <li
              key={a.id}
              className={`rounded-md border px-3 py-1.5 ${
                a.estado === 'resuelta'
                  ? 'border-linea bg-papel text-apagado'
                  : a.vencida
                    ? 'border-alerta/40 bg-red-50'
                    : a.visible
                      ? 'border-dorado/50 bg-dorado-claro'
                      : 'border-linea'
              }`}
            >
              <div className="flex justify-between gap-2">
                <span className="font-medium">{a.titulo}</span>
                <span className="flex shrink-0 gap-2 text-xs">
                  {a.estado === 'resuelta'
                    ? tr('Resuelta', 'Resolved')
                    : a.vencida
                      ? tr('Vencida', 'Overdue')
                      : tr('Activa', 'Active')}
                  {permisos.editar && (
                    <button type="button" onClick={() => void cambiarEstado(a)} className="text-verde hover:underline">
                      {a.estado === 'resuelta' ? tr('Reabrir', 'Reopen') : `✓ ${tr('Resolver', 'Resolve')}`}
                    </button>
                  )}
                </span>
              </div>
              <div className="text-xs text-apagado">
                {fecha(a.fecha)} · {a.tipoEvento} ·{' '}
                {tr(`aviso ${numero(a.diasAntes)} días antes`, `notice ${numero(a.diasAntes)} days before`)}
                {a.notas ? ` · ${a.notas}` : ''}
                {a.codigos.length > 1 && (
                  <>
                    {' · '}
                    {tr('También: ', 'Also: ')}
                    {a.codigos
                      .filter(c => c !== codigo)
                      .map((c, i) => (
                        <span key={c}>
                          {i > 0 && ', '}
                          <Link to={`/seguimiento/${encodeURIComponent(c)}`} className="text-verde hover:underline">
                            {c}
                          </Link>
                        </span>
                      ))}
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
